// frontend/src/components/layout/Footer.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import packageJson from '../../../package.json';
import { fetchHealth } from '@api/health';
import { classifyHealthResult } from '@core/domain/health';
import { branding } from '@core/config/branding';
import { useRegion } from '../../contexts/RegionContext';
import StatusDot from '../common/StatusDot';

const appVersion = import.meta.env.VITE_APP_VERSION || packageJson.version;

const STATUS = {
  checking:    { color: 'var(--status-amber)', label: 'Checking' },
  operational: { color: 'var(--live-green)',   label: 'Systems Operational' },
  degraded:    { color: 'var(--status-amber)', label: 'Degraded' },
  unreachable: { color: 'var(--live-red)',     label: 'Unreachable' },
};

const linkStyle = {
  color: 'var(--text-secondary)',
  textDecoration: 'none',
  transition: 'color 0.15s',
};

const h4Style = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-primary)',
  marginBottom: 10,
};

const ulStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
};

function Sep() {
  return <span style={{ color: 'var(--border)', userSelect: 'none' }}>·</span>;
}

export default function Footer() {
  const { adapter } = useRegion();
  const [status, setStatus] = useState('checking');
  const [expanded, setExpanded] = useState(
    () => localStorage.getItem('footerExpanded') === 'true'
  );

  useEffect(() => {
    localStorage.setItem('footerExpanded', String(expanded));
  }, [expanded]);

  useEffect(() => {
    let cancelled = false;
    const checkHealth = async () => {
      const result = await fetchHealth();
      if (cancelled) return;
      setStatus(classifyHealthResult(result));
    };
    checkHealth();
    const id = setInterval(checkHealth, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const currentYear = new Date().getFullYear();
  const s = STATUS[status] ?? STATUS.checking;

  const onLinkEnter = (e) => { e.currentTarget.style.color = 'var(--text-primary)'; };
  const onLinkLeave = (e) => { e.currentTarget.style.color = 'var(--text-secondary)'; };

  return (
    <footer
      style={{
        flexShrink: 0,
        background: 'var(--bg-surface)',
        borderTop: '1px solid var(--border)',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* ---- Expanded panel (animated, grows upward) ---- */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: expanded ? '1fr' : '0fr',
          transition: 'grid-template-rows 220ms ease',
        }}
      >
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div style={{ padding: '22px 20px 18px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32 }}>

              {/* Col 1: Brand */}
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                  {branding.name}
                </h3>
                <p style={{ fontSize: 12, marginTop: 6, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {branding.tagline}
                </p>
                <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <StatusDot color={s.color} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: s.color, letterSpacing: '0.03em' }}>
                    {s.label}
                  </span>
                </div>
              </div>

              {/* Col 2: Legal & Company */}
              <div>
                <h4 style={h4Style}>Legal &amp; Company</h4>
                <ul style={ulStyle}>
                  <li><Link to="/about"   style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>About</Link></li>
                  <li><Link to="/privacy" style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>Privacy Policy</Link></li>
                  <li><Link to="/terms"   style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>Terms &amp; Conditions</Link></li>
                  <li><Link to="/cookies" style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>Cookie Policy</Link></li>
                  <li><Link to="/gdpr"    style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>GDPR</Link></li>
                </ul>
              </div>

              {/* Col 3: Consulting & Support */}
              <div>
                <h4 style={h4Style}>Consulting &amp; Support</h4>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Need a hand on your digital journey?
                </p>
                <a
                  href={branding.companyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: 10,
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'var(--accent)',
                    color: 'var(--text-on-accent)',
                    textDecoration: 'none',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--accent)'; }}
                >
                  Our consulting offerings →
                </a>
                <ul style={{ ...ulStyle, marginTop: 12 }}>
                  <li>
                    <a href={`mailto:${branding.contactEmail}`} style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>
                      {branding.contactEmail}
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Legal disclaimers */}
            <div
              style={{
                marginTop: 18,
                paddingTop: 12,
                borderTop: '1px solid var(--border-subtle)',
                fontSize: 10,
                lineHeight: 1.7,
                color: 'var(--text-muted)',
              }}
            >
              {adapter.disclaimer}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Always-visible strip ---- */}
      <div
        style={{
          height: 36,
          padding: '0 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          fontSize: 11,
          color: 'var(--text-muted)',
          borderTop: expanded ? '1px solid var(--border-subtle)' : '1px solid transparent',
        }}
      >
        {/* Status */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <StatusDot color={s.color} pulse={status === 'operational'} />
          <span style={{ color: s.color, fontWeight: 700, letterSpacing: '0.04em' }}>
            {s.label.toUpperCase()}
          </span>
        </span>

        <Sep />
        <span>© {currentYear} {branding.copyrightHolder}</span>
        <Sep />

        <Link to="/privacy" style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>Privacy</Link>
        <Link to="/gdpr"    style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>GDPR</Link>
        <Link to="/terms"   style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>Terms</Link>
        <Link to="/about"   style={linkStyle} onMouseEnter={onLinkEnter} onMouseLeave={onLinkLeave}>About</Link>

        <div style={{ flex: 1 }} />

        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>
          v{appVersion}
        </span>

        <button
          onClick={() => setExpanded(e => !e)}
          title={expanded ? 'Collapse footer' : 'Expand footer'}
          aria-expanded={expanded}
          style={{
            width: 24, height: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            transition: 'color 0.15s, border-color 0.15s',
            flexShrink: 0,
            padding: 0,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = 'var(--text-primary)';
            e.currentTarget.style.borderColor = 'var(--text-muted)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = 'var(--text-secondary)';
            e.currentTarget.style.borderColor = 'var(--border)';
          }}
        >
          <svg
            width="12" height="12" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      </div>
    </footer>
  );
}